import React from 'react';
import { Sparkles, Wand2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contact } from '../i18n/strings';

const HERO_IMG =
  'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80';

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="hero"
      className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden bg-gradient-hero"
    >
      <div className="absolute inset-0 bg-gradient-mesh opacity-60" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,#09070f_95%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div className="space-y-7 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-full px-4 py-2">
              <Wand2 className="w-4 h-4 text-pink" />
              <span className="text-primary-light text-sm font-semibold tracking-wide">{t.hero.badge}</span>
              <Sparkles className="w-4 h-4 text-accent" />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white font-heading leading-[1.05] tracking-tight">
              Digi<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light via-pink to-accent">Painto</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl border-l-2 border-primary/40 pl-5">
              {t.hero.tagline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#gallery"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-pink hover:from-primary-light hover:to-pink text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-glow hover:-translate-y-0.5"
              >
                {t.headerCta}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 border border-border-dark hover:border-accent/50 text-slate-200 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all bg-card-dark/40"
              >
                {t.nav.features}
              </a>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed max-w-xl">{t.hero.priceNote}</p>
            <p className="text-[11px] text-slate-600 leading-relaxed max-w-2xl">{t.hero.legalLong}</p>
          </div>

          <div className="relative order-1 lg:order-2">
            <div className="relative aspect-[4/5] max-h-[520px] mx-auto max-w-md lg:max-w-none rounded-[2rem] overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/50 bg-gradient-to-br from-primary/30 to-pink/20">
              <img
                src={HERO_IMG}
                alt={t.hero.badge}
                className="w-full h-full object-cover scale-105"
                width={600}
                height={750}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/90 via-transparent to-transparent" />
              <div className="absolute top-6 right-6">
                <div className="bg-primary/20 backdrop-blur border border-primary/30 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="text-xs text-primary-light font-semibold">AI Online</span>
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                <div className="bg-card-dark/95 backdrop-blur border border-border-dark rounded-2xl p-4 shadow-xl max-w-[240px]">
                  <p className="text-white font-bold text-sm">{t.hero.badge}</p>
                  <p className="text-slate-400 text-xs mt-1">{t.brandDomain}</p>
                </div>
                <div className="bg-gradient-to-r from-primary to-pink text-white font-bold text-sm px-4 py-3 rounded-xl shadow-lg">
                  {contact.pricePerWeek}/v
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
